<script setup>
import { computed, ref, unref } from 'vue'

import { getIcon } from '@/tiptap/constants/icons'
import Logger from '@/tiptap/utils/logger'

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  upload: { type: Function, default: undefined },
  t: { type: Function, required: true }
})

const emit = defineEmits(['update:modelValue'])

const loading = ref(false)
const form = computed({
  get: () => props.modelValue,
  set: val => emit('update:modelValue', val)
})

const onFileSelected = async (files) => {
  const file = files instanceof File ? files : (Array.isArray(files) ? files[0] : null)
  if (!file) {
    // User cleared the input – don't throw; clear state and exit
    form.value = { ...unref(form), file: null, src: undefined }
    return
  }

  if (file.size > 5 * 1024 * 1024) {
    alert('Image is larger than 5 MB')
    return
  }

  try {
    loading.value = true
    const data = await (props.upload ? props.upload(file) : null)
    if (!data) {
      throw new Error('No link received after upload')
    }

    form.value = {
      ...unref(form),
      src: data
    }
  } catch (err) {
    Logger.error(`Failed to execute upload file: ${err}`)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <VForm :disabled="loading">
    <VFileInput
      v-model="form.file"
      :label="t('editor.image.dialog.form.file')"
      accept="image/*"
      :loading="loading"
      :prepend-icon="getIcon('fileImagePlus')"
      @update:model-value="onFileSelected"
      @click:clear="() => { form.file = null; form.src = undefined }"
    />

    <VTextField
      v-model="form.src"
      :label="t('editor.image.dialog.form.link')"
      disabled
      autofocus
      :prepend-icon="getIcon('linkVariant')"
    />

    <VTextField
      v-model="form.alt"
      :label="t('editor.image.dialog.form.alt')"
      :prepend-icon="getIcon('text')"
    />

    <VCheckbox
      v-model="form.lockAspectRatio"
      :label="t('editor.image.dialog.form.aspectRatio')"
    />
  </VForm>
</template>